import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const SUBMIT_URL = 'https://readdy.ai/api/form/d6j4rijcs59vsrrrjspg';
const STORAGE_KEY = 'meow_welcome_popup_dismissed';
const STORAGE_VERSION = 'v1'; // Increment this to reset for all users

export default function WelcomeEmailPopup() {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    // Only show if not dismissed OR if it's an old version
    if (!dismissed || dismissed !== STORAGE_VERSION) {
      const timer = setTimeout(() => setVisible(true), 4000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem(STORAGE_KEY, STORAGE_VERSION);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus('submitting');
    try {
      const params = new URLSearchParams();
      params.append('email', email);
      params.append('name', name);
      params.append('source', 'welcome_popup');
      const res = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus('success');
        localStorage.setItem(STORAGE_KEY, STORAGE_VERSION);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={handleDismiss}
          />

          {/* Modal */}
          <motion.div
            className="relative z-10 w-full max-w-lg bg-white rounded-3xl overflow-hidden shadow-2xl"
            initial={{ scale: 0.85, y: 40, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.85, y: 40, opacity: 0 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          >
            {/* Close */}
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-gray-500 hover:text-gray-900 transition-all cursor-pointer shadow"
            >
              <i className="ri-close-line text-lg"></i>
            </button>

            {/* Top image strip */}
            <div className="relative h-44 w-full overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/b6fc7fa88b731afc5db79280788e2005.jpg"
                alt="Cute kittens"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white via-white/10 to-transparent" />
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
                {['🐾','🐱','🐾'].map((e, i) => (
                  <span key={i} className="text-xl">{e}</span>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="px-8 pb-8 -mt-2">
              {status === 'success' ? (
                <div className="text-center py-6">
                  <div className="text-5xl mb-3">🎉</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">You're in the Meow Squad!</h3>
                  <p className="text-gray-500 text-sm mb-5">
                    Check your inbox — your <strong>Free Cat Care Guide</strong> is on its way!
                  </p>
                  <div className="flex flex-col gap-2">
                    <Link
                      to="/cat-care-guide"
                      onClick={handleDismiss}
                      className="px-6 py-2.5 bg-teal-500 hover:bg-teal-600 text-white font-bold rounded-full text-sm transition-all cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-2"
                    >
                      <i className="ri-book-open-line"></i> View the Cat Care Guide
                    </Link>
                    <button
                      onClick={handleDismiss}
                      className="px-6 py-2 text-gray-400 hover:text-gray-600 font-medium text-sm transition-all cursor-pointer whitespace-nowrap"
                    >
                      Start Exploring 🐱
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-center mb-5">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-teal-100 text-teal-700 text-xs font-bold rounded-full mb-3">
                      <i className="ri-gift-line"></i>
                      FREE GIFT FOR YOU
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-1 leading-tight">
                      Get the Ultimate<br />
                      <span className="text-teal-500">Cat Care Guide</span> — Free!
                    </h2>
                    <p className="text-gray-500 text-sm mt-2">
                      Join <strong>12,400+</strong> cat lovers. Get weekly videos, exclusive deals & expert tips delivered to your inbox.
                    </p>
                  </div>

                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="space-y-3"
                  >
                    <input
                      type="text"
                      name="name"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="Your first name"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                    />
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      placeholder="Your email address"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                    />
                    {status === 'error' && (
                      <p className="text-rose-500 text-xs">Something went wrong. Please try again.</p>
                    )}
                    <button
                      type="submit"
                      disabled={status === 'submitting'}
                      className="w-full py-3.5 bg-teal-500 hover:bg-teal-600 disabled:bg-gray-200 text-white font-bold rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-lg shadow-teal-200"
                    >
                      {status === 'submitting' ? (
                        <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                      ) : (
                        <><i className="ri-mail-send-line"></i> Send Me the Free Guide!</>
                      )}
                    </button>
                  </form>

                  <div className="flex items-center justify-center gap-4 mt-4">
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <i className="ri-lock-line text-teal-400"></i> No spam, ever
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <i className="ri-close-circle-line text-teal-400"></i> Unsubscribe anytime
                    </div>
                  </div>

                  <div className="flex flex-col items-center gap-1 mt-3">
                    <Link
                      to="/cat-care-guide"
                      onClick={handleDismiss}
                      className="text-xs text-teal-500 hover:text-teal-700 font-medium cursor-pointer transition-colors"
                    >
                      See what's inside the guide →
                    </Link>
                    <button
                      onClick={handleDismiss}
                      className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
                    >
                      No thanks, I don't want free cat tips
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}